// 職責：雙層認證狀態（ops 集團層 + 門店層）的單一事實來源，並由此推導按鈕權限

/*
 * 為什麼是「雙 token」而不是一個（doc 09 §3.1）：
 *   ops JWT 用 Ops:JwtSigningKey 簽、只認 /ops/api/*；門店 JWT 用 legacy AppSettings:Secret 簽、
 *   只認該店 webapi。control-plane 刻意永不載入 legacy secret，所以兩者**永遠不可互換**。
 *   把它們放進同一個 store 是為了讓「清乾淨」有單一位置（doc 04 §3.4 的缺陷 #11 正是清不乾淨）。
 *
 * 權限（authz v2，2026-09-03）：
 *   登入回應的 `functions[]`（type=5 `shopmgr-*` 列 + type=2 POS 碼）經 `domain/permissions`
 *   反查為目錄 key、展開閉包，存為 `permissions: PermissionState`。前端據此決定側欄／分頁／
 *   按鈕是否顯示（`app/permissions`），而**同一批店碼**亦是門店 webapi `[RequirePermission]`
 *   filter 檢查的對象 —— 後端初期採用 log 模式（只記錄不阻擋），soak 完才 enforce（plan §7.2 / §8.2）。
 *   ★ 這一版 fail-closed：權限集為空就確實什麼都沒有，沒有「空⇒全放行」後備（D7）。
 *     唯一例外是 `custom_flag='99'` 超級用戶（後端本身回傳全部功能）。
 */

import { defineStore } from 'pinia'
import { z } from 'zod'

import { PosType } from '@/domain/enums'
import { isKnown, type PermissionKey } from '@/domain/permissions/catalog'
import {
  buildPermissionState,
  hasPermission,
  toPermissionSet,
  type PermissionState,
} from '@/domain/permissions/permissionSet'
import { isJwtExpired } from '@/shared/data/jwt'
import type { StoreProfile } from '@/domain/rules/wording'
import type { FunctionNode, Operator, OpsStore, StoreUser } from '@/domain/types'

/* ────────────────────────────── 對外型別 ────────────────────────────── */

/**
 * 門店登入（`POST {url}users/authenticate`）**經 adapter 轉換後**的結果。
 * 刻意收 camelCase 領域形狀而非後端原始 snake_case：doc 06 §1 要求 snake_case 止於 adapter 邊界，
 * store 若自行解析後端欄位，型別收斂就會有第二個來源。
 */
export interface StoreAuthResult {
  /** 門店 JWT（8 小時，只含一個 claim `id`）*/
  token: string
  user: StoreUser
  /** `merchant_function` 全表（含選單、按鈕等各 type）；本 store 只讀 type=5 `shopmgr-*` 同 type=2 POS 碼 */
  functions: FunctionNode[]
}

/* ────────────────────────────── 密碼（O1 待裁決）★ ────────────────────────────── */

/*
 * doc 09 §3.4 方案 A：集團登入的密碼保留在記憶體，讓選店/切店不必再輸入一次。
 * 這裡刻意**放在模組層級的非 reactive 變數**，理由有三：
 *   ① 不進 store state → devtools 的狀態面板、時光旅行快照、任何 $subscribe 都看不到它
 *   ② 不進 reactive → 不會被誤 watch 進其他衍生狀態或意外持久化外掛
 *   ③ 頁面重整即消失，符合「絕不寫入任何持久化儲存」的取捨
 * ✅ O1 已於 2026-07-23 裁決採方案 A（doc 09 §3.4）：集團登入與門店登入用的是**同一組憑證**，
 *    再問一次不會增加任何安全性，只把成本加在合法使用者身上。M1 據此實作：
 *      - `useAuth().loginGroup` 成功後呼叫 remember，`enterShop` 以 peek 取用（防護欄 #1）
 *      - 頁面重整即遺失 → 選店頁會要求重新輸入一次（防護欄 #2）
 *      - 30 分鐘閒置自動登出，見 `features/auth/composables/useIdleLogout`（防護欄 #3）
 *      - `logout()` 一併 clearGroupPassword（防護欄 #5）
 */
let groupPassword: string | null = null

/** 快速存取 bearer 只留在目前 tab；關 tab 即失效，亦不會與其他瀏覽器 profile 共用。 */
const KEY_QUICK_ACCESS = 'shopmgr-quick-access'

/** 暫存集團密碼於記憶體。⚠️ 絕不 log、絕不持久化。 */
export function rememberGroupPassword(password: string): void {
  groupPassword = password
}

/** 取用暫存的集團密碼（供選店後自動完成門店登入）*/
export function peekGroupPassword(): string | null {
  return groupPassword
}

export function clearGroupPassword(): void {
  groupPassword = null
}

export function rememberQuickAccessToken(token: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(KEY_QUICK_ACCESS, token)
  } catch {
    // 隱私模式拒絕 sessionStorage 時仍可完成首次登入；只是重載後切店要重新開連結
  }
}

export function peekQuickAccessToken(): string | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(KEY_QUICK_ACCESS)
  } catch {
    return null
  }
}

export function clearQuickAccessToken(): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(KEY_QUICK_ACCESS)
  } catch {
    // 同上；登出仍會清 Pinia/localStorage session
  }
}

/* ────────────────────────────── 登出 hook ────────────────────────────── */

type LogoutHook = () => void
const logoutHooks = new Set<LogoutHook>()

/**
 * 註冊登出時要執行的清理（doc 09 §3.5 的第三層：TanStack Query 快取）。
 * 為什麼用 hook 而不是在 store 內 import queryClient：
 *   store 屬於 UI 狀態層，import 資料層會造成環狀相依，也會讓單元測試被迫準備 Query 環境。
 * @returns 取消註冊的函式
 */
export function onLogout(hook: LogoutHook): () => void {
  logoutHooks.add(hook)
  return () => {
    logoutHooks.delete(hook)
  }
}

/* ────────────────────────────── 持久化 ────────────────────────────── */

/*
 * ★ 沿用 legacy 的 key 名（doc 04 §3.4 的 AUTH_KEYS）而非另取新名。
 *   理由：logout() 必須把「所有認證衍生的 key」在單一位置清掉。沿用同一組 key 表示
 *   新版寫入的與舊版殘留的是同一批，不會出現「清單漏列了新 key」這種只在升級後才爆的缺陷。
 *   'local-taiyi' 新版不寫入，但仍列入清單以清除舊版殘留。
 *   'quanxian' 是 2026-09-03 之前的權限表 key（authz v2 改用 'shopmgr-perms'）—— 同樣只為清理而保留。
 */
const AUTH_KEYS = [
  'taiyi-shopmgr',
  'ops-session',
  'ops-stores',
  'shopmgr-perms',
  'quanxian',
  'local-taiyi',
  'url_taiyi',
] as const

const KEY_OPS_SESSION = 'ops-session'
const KEY_OPS_STORES = 'ops-stores'
const KEY_SHOP = 'url_taiyi'
const KEY_STORE_SESSION = 'taiyi-shopmgr'
const KEY_PERMISSIONS = 'shopmgr-perms'

/** localStorage 在 SSR、單元測試（node 環境）與隱私模式下都可能不存在或拋錯 */
function readRaw(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeRaw(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value)
  } catch {
    // 配額用盡或隱私模式：持久化失敗不應中斷登入流程，記憶體狀態仍然有效
  }
}

function removeRaw(key: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(key)
  } catch {
    // 同上
  }
}

/*
 * localStorage 的內容是**使用者可竄改的外部輸入**（doc 04 §1 事實 3），
 * 且可能是舊版寫入的不同形狀。因此還原一律經 schema 驗證，失敗就當作沒有登入過。
 */
const operatorSchema = z.object({
  id: z.number(),
  account: z.string(),
  fullName: z.string(),
  isPlatform: z.boolean(),
})

const opsStoreSchema = z.object({
  mid: z.number(),
  name: z.string(),
  url: z.string(),
  uploadUrl: z.string(),
  area: z.string(),
  category: z.string(),
  seq: z.number(),
  isEnable: z.boolean(),
  currency: z.string().nullable(),
  roleCode: z.string().nullable(),
  isSuperuser: z.boolean(),
  /*
   * ★ optional + default：這欄 2026-08-21 才加。
   *   寫成必填的話，**上一次登入寫入 localStorage 的那份**會解析失敗 ——
   *   部署當日全部在線使用者會被靜靜地踢回登入頁。
   */
  retailBusiness: z.boolean().default(false),
})

const opsSessionSchema = z.object({
  opsToken: z.string(),
  operator: operatorSchema,
})

const storeUserSchema = z.object({
  id: z.number(),
  username: z.string(),
  fullName: z.string(),
  avatar: z.string().nullable(),
  merchantId: z.number(),
  deptId: z.number().nullable(),
  panelId: z.number().nullable(),
  posType: z.union([z.literal(PosType.Food), z.literal(PosType.Retail)]),
  /** ★ 與 `retailBusiness` 一樣是後加欄，舊 localStorage 沒有 → 收斂成 null */
  businessCategory: z.number().nullable().default(null),
  isSuperUser: z.boolean(),
})

const storeSessionSchema = z.object({
  storeToken: z.string(),
  user: storeUserSchema,
})

/**
 * `shopmgr-perms` 的形狀。`v` 是版本欄：日後改形狀就 bump，舊 build 寫入的會直接驗證不通過
 * → 視為沒有權限 → fail-closed 送去重新登入，不會靜默地用錯形狀。
 */
const permissionsSchema = z.object({
  v: z.literal(1),
  granted: z.array(z.string()),
  unknownCodes: z.array(z.string()),
})

/*
 * ★ `z.ZodType<T, z.ZodTypeDef, unknown>` 而不是 `z.ZodType<T>`：
 *   後者等於 Input = Output = T，一遇到有 `.default()` 的 schema（輸入可缺、輸出必有），
 *   TS 就會由 **input** 那邊推 T，令還原出來的型別多了一堆 `| undefined`。
 *   與 `api/resources/report.ts` 的 `fetchRows` 同一寫法。
 */
function restore<T>(key: string, schema: z.ZodType<T, z.ZodTypeDef, unknown>): T | null {
  const raw = readRaw(key)
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    const result = schema.safeParse(parsed)
    return result.success ? result.data : null
  } catch {
    return null
  }
}

/**
 * 還原門店 session，**但過了期的 token 一律當沒有登入過**（2026-09-18 浸會 801 上載失敗）。
 *
 * 🚨 為什麼一定要在這裡攔：門店 JWT 只有 8 個小時壽命，而後端**只有 `users` 與 `FileUpload`
 *    兩支 API 套了 `[Authorize]`** —— 一張失效的 token 在畫面上零病徵（餐牌照改、
 *    設定照存），直到有人按「上載相片」才吃 401，而他只會覺得「上載壞了」。
 *    現場真的遇過：機器開機還原前一日的 localStorage，連按四次上載全部 401。
 *
 * 順便清走 localStorage：不清的話下次重整又會再解析一次同一張失效 token。
 * 權限表一併清 —— 它是隨那次門店登入取回來的，沒有了 token 就沒有意義。
 */
function restoreLiveStoreSession(): { storeToken: string; user: StoreUser } | null {
  const session = restore(KEY_STORE_SESSION, storeSessionSchema)
  if (session === null) return null
  if (!isJwtExpired(session.storeToken)) return session
  removeRaw(KEY_STORE_SESSION)
  removeRaw(KEY_PERMISSIONS)
  return null
}

/* ────────────────────────────── 權限還原 ────────────────────────────── */

/** 新登入／切店／登出後的空權限。每次新建一個：frozen 常數放入 reactive state 會被 Vue 跳過代理 */
function emptyPermissions(): PermissionState {
  return { granted: [], unknownCodes: [] }
}

/**
 * 由 localStorage 還原權限。
 * ★ 剔除目錄已經不認得的 key：升級 build 之後舊 key 可能已改名／刪除，
 *   保留它們只會令 `permissionSet` 裡面有永遠對不上任何閘的字串。
 *   （不會因此放寬任何東西 —— 少了 key 只會少了權限，方向是安全的。）
 */
function restorePermissions(): PermissionState | null {
  const parsed = restore(KEY_PERMISSIONS, permissionsSchema)
  if (parsed === null) return null
  return {
    granted: parsed.granted.filter(isKnown),
    unknownCodes: [...parsed.unknownCodes],
  }
}

function persistPermissions(state: PermissionState): void {
  writeRaw(
    KEY_PERMISSIONS,
    JSON.stringify({ v: 1, granted: state.granted, unknownCodes: state.unknownCodes }),
  )
}

/* ────────────────────────────── Store ────────────────────────────── */

interface SessionState {
  /** ops JWT（12 小時），只用於 `/ops/api/*` */
  opsToken: string | null
  operator: Operator | null
  /** 本操作員有權進入的門店（已由後端按 seq 排序，且只含 sync_state='synced' 者）*/
  stores: OpsStore[]
  /** 當前門店；null 表示尚未選店 */
  shop: OpsStore | null
  /** 門店 JWT（8 小時），只用於該店 webapi */
  storeToken: string | null
  user: StoreUser | null
  /** ★ 租戶識別來自登入回應而非 JWT claim（doc 02 §1.7）*/
  merchantId: number | null
  /** 決定 i18n 詞彙包（餐飲／零售）*/
  posType: PosType
  /** 已展開閉包的目錄 key + 目錄不認得的 `shopmgr-*` 店碼（見 domain/permissions/permissionSet）*/
  permissions: PermissionState
}

export const useSessionStore = defineStore('session', {
  state: (): SessionState => {
    const ops = restore(KEY_OPS_SESSION, opsSessionSchema)
    const restoredStore = restoreLiveStoreSession()
    const shop = restore(KEY_SHOP, opsStoreSchema)
    const restoredPerms = restorePermissions()

    /*
     * ★★ fail-closed 還原：有門店 session 但沒有／損壞的權限 ⇒ 視為門店 session 不存在。
     *   典型情況是升級日 —— 上一個 build 寫的是 `quanxian`，新 build 讀 `shopmgr-perms` 讀不到。
     *   如果照樣還原 token 而權限是空集，使用者會見到一個只有「概覽」的側欄，
     *   而且每個按鈕都不見了，沒有任何提示說明原因。丟棄門店 session 的話 router 守衛會送去
     *   `/login?redirect=`，重新登入一次即取回完整權限 —— 這是唯一不會令人一頭霧水的出路。
     *   （反方向不需要處理：有權限沒有 token，`isAuthed` 已經是 false。）
     */
    const storeSession = restoredStore !== null && restoredPerms !== null ? restoredStore : null
    if (restoredStore !== null && restoredPerms === null) removeRaw(KEY_STORE_SESSION)

    return {
      opsToken: ops?.opsToken ?? null,
      operator: ops?.operator ?? null,
      stores: restore(KEY_OPS_STORES, z.array(opsStoreSchema)) ?? [],
      shop,
      storeToken: storeSession?.storeToken ?? null,
      user: storeSession?.user ?? null,
      merchantId: storeSession?.user.merchantId ?? null,
      posType: storeSession?.user.posType ?? PosType.Food,
      permissions:
        storeSession !== null && restoredPerms !== null ? restoredPerms : emptyPermissions(),
    }
  },

  getters: {
    /**
     * 已完整進入某一間店（ops 登入 + 選店 + 門店登入三者皆備）才算可操作業務頁。
     *
     * ★ 連「token 未過期」都要計：門店 JWT 只有 8 個小時壽命，一個開足一日的分頁會靜靜地
     *   變成殭屍 session —— 畫面全部照用（後端業務端點沒有 `[Authorize]`），但一按上載
     *   相片就 401。路由守衛讀這個 getter，所以下一次導頁就會彈回登入頁。
     *   （導頁之前就過了期的那段時間由 `useSessionExpiry` 及全域 401 兜底。）
     */
    isAuthed(state): boolean {
      return state.storeToken !== null && state.shop !== null && !isJwtExpired(state.storeToken)
    },

    /** 只完成集團登入（可看門店清單，尚不能打業務 API）*/
    isOpsAuthed(state): boolean {
      return state.opsToken !== null
    },

    /**
     * 業務 API 基址。空字串表示尚未選店 —— 呼叫端應先檢查 `isAuthed`。
     * 補結尾斜線的理由同 runtime-config：下游一律「base + 相對路徑」拼接。
     */
    baseUrl(state): string {
      return withTrailingSlash(state.shop?.url)
    },

    /**
     * 圖片上傳基址 —— **一律就是門店 API 基址**。
     *
     * 2026-08-20 追查浸會上傳失敗，發現 `ops_store.upload_host` 根本不是「上傳到哪裡」：
     * 它真正的消費者是後端 —— `TemplateService.cs:45` 拿它做 webapi 的
     * `AppSettings.UploadHost`（後端組裝回給客戶端看的那條**圖片絕對網址**），
     * `TemplateService.cs:77` 拿它做 printserver 的 `imgUri`。即是「圖片 serve 基址」。
     *
     * 而 `FileUpload` 這支 API 由頭到尾都位於門店自己的 webapi（與 `/api/*` 同一個
     * host、同一個 JWT）。所以上傳不應該多讀一個設定 —— 多一個設定就多一個填錯的機會：
     *   - 浸會兩間店 `upload_host` 填了一個裸 IP `192.168.3.50` → 拼出相對網址 → SPA
     *     fallback 回 HTML，按鈕看似正常但一按必錯；
     *   - `AllocService.cs:97` 開新店寫的是 `…/{brand}/images/`（圖片基址）→ 每間新店
     *     都會遇到同一問題。
     *
     * 見 webapi/doc/goods-image-unify_zh.md。
     */
    uploadUrl(state): string {
      return withTrailingSlash(state.shop?.url)
    },

    /**
     * 零售／餐飲判定所需的四個訊號，組裝成一份 `StoreProfile`（見 `domain/rules/wording`）。
     *
     * ★ 刻意不在這裡做判定、只是集齊訊號：判定規則要與 scpos `shop-mode.ts` 逐條對得上，
     *   而那條規則屬於 domain（純函式、測得到），不屬於 store。
     * ⚠️ 四個訊號來自**兩個不同來源** —— `retailBusiness` 與 `webapiBaseUrl` 是 ops registry
     *   （選店時取得），`posType` / `businessCategory` / `merchantId` 是門店登入回應。
     *   未選店 / 未登入時各自退回安全預設（＝當餐飲）。
     */
    storeProfile(state): StoreProfile {
      return {
        posType: state.posType,
        businessCategory: state.user?.businessCategory ?? null,
        retailBusiness: state.shop?.retailBusiness ?? false,
        merchantId: state.merchantId,
        webapiBaseUrl: state.shop?.url ?? '',
      }
    },

    /** 貨幣符號 —— ops 投影缺此欄，登入後由店內 shop_config 補上（doc 09 §4.2）*/
    currency(state): string | null {
      return state.shop?.currency ?? null
    },

    /** 本操作員在當前門店的 ops 角色代碼，供門店切換器顯示角色標籤（doc 09 §6.2）*/
    roleCode(state): string | null {
      return state.shop?.roleCode ?? null
    },

    /** 已授權的目錄 key 集合（閉包已展開）。router 守衛、側欄、`usePermission` 全部讀取這個 */
    permissionSet(state): ReadonlySet<PermissionKey> {
      return toPermissionSet(state.permissions)
    },

    /**
     * 超級用戶（`custom_flag === '99'`）：後端登入時回**全部** enabled functions（Utils.cs:1129-1132），
     * 前端所有閘一律直接通過。這是 D7 fail-closed 之下唯一保留的「全開」路徑。
     */
    isSuperUser(state): boolean {
      return state.user?.isSuperUser ?? false
    },

    /**
     * 是否擁有某個權限 key。無權時呼叫端一律用 `v-if` 隱藏而非 `disabled`（doc 04 §3.3）——
     * 顯示一個按下去會 403 的按鈕只會令人反覆嘗試。
     * ★ 同一條判斷亦在 `api/context.requirePermission` 於 mutationFn 開頭再做一次：
     *   UI 漏了閘時，寫入路徑也會阻擋。
     */
    has(): (key: PermissionKey) => boolean {
      const set = this.permissionSet
      const superUser = this.isSuperUser
      return (key) => hasPermission(set, key, superUser)
    },
  },

  actions: {
    /** ① 集團登入成功後寫入 ops 層狀態 */
    setOpsSession(opsToken: string, operator: Operator, stores: OpsStore[]): void {
      this.opsToken = opsToken
      this.operator = operator
      this.stores = stores
      writeRaw(KEY_OPS_SESSION, JSON.stringify({ opsToken, operator }))
      writeRaw(KEY_OPS_STORES, JSON.stringify(stores))
    },

    /**
     * ② 選定當前門店。
     * 只設門店本身，**不設 storeToken** —— 門店 JWT 要另外打該店的 users/authenticate 才有。
     * ⚠️ 切店等於換一個後端資料庫，呼叫端必須同時清空 Query 快取（doc 02 §1.1）。
     */
    selectShop(shop: OpsStore): void {
      const changed = this.shop?.mid !== shop.mid
      this.shop = shop
      if (changed) {
        // 舊店的門店 token 對新店無效（不同 DB、不同 admin_employee.id），必須作廢
        this.storeToken = null
        this.user = null
        this.merchantId = null
        this.permissions = emptyPermissions()
        removeRaw(KEY_STORE_SESSION)
        removeRaw(KEY_PERMISSIONS)
      }
      writeRaw(KEY_SHOP, JSON.stringify(shop))
    },

    /** ③ 門店登入成功後寫入店內層狀態並由 `functions[]` 構建權限集 */
    setStoreSession(auth: StoreAuthResult): void {
      this.storeToken = auth.token
      this.user = auth.user
      this.merchantId = auth.user.merchantId
      this.posType = auth.user.posType
      this.permissions = buildPermissionState(auth.functions)

      // ★ 先寫權限、後寫門店 session：state() 還原時「有 session 沒有權限」會被當成沒有登入，
      //   兩個 write 之間如果剛好遇上重整，寧願少了權限（要重新登入）也不要變成「有 token 沒有權限」。
      persistPermissions(this.permissions)
      writeRaw(KEY_STORE_SESSION, JSON.stringify({ storeToken: auth.token, user: auth.user }))
    },

    /**
     * 補上登入時取不到的貨幣符號（來源：店內 `GET api/shop_config/list`，doc 09 §4.2）。
     * 寫回 `shop` 而非另開 state，讓「當前門店」始終只有一份。
     */
    setCurrency(currency: string | null): void {
      if (this.shop === null) return
      this.shop = { ...this.shop, currency }
      writeRaw(KEY_SHOP, JSON.stringify(this.shop))
    },

    /**
     * ★ 登出必須清三層（doc 09 §3.5）：記憶體、localStorage、伺服器狀態快取。
     * 舊版只清一個 key 且 opsLogout() 從無呼叫點（缺陷 #11）—— 殘留的 token 會讓
     * 下一位使用者在同一台機器上「半登入」，看到上一位的門店清單。
     */
    logout(): void {
      // ① localStorage：所有認證衍生的 key，含舊版殘留。
      //    刻意先於記憶體清除 —— $reset() 之類的還原邏輯會重讀 localStorage，
      //    順序反過來就會把剛清掉的狀態又還原回來。
      for (const key of AUTH_KEYS) removeRaw(key)

      // ② 記憶體：密碼與全部 state（逐欄指派而非 $reset()，因 state() 會重讀儲存）
      clearGroupPassword()
      clearQuickAccessToken()
      this.opsToken = null
      this.operator = null
      this.stores = []
      this.shop = null
      this.storeToken = null
      this.user = null
      this.merchantId = null
      this.posType = PosType.Food
      this.permissions = emptyPermissions()

      // ③ 交由呼叫端清 Query 快取／導向登入頁
      for (const hook of logoutHooks) hook()
    },
  },
})

/** 空值一律回空字串（而非 undefined），讓呼叫端只需檢查 falsy 一種情況 */
function withTrailingSlash(value: string | undefined): string {
  if (value === undefined) return ''
  const trimmed = value.trim()
  if (trimmed === '') return ''
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`
}
